import { useEffect, useState } from "react";
import {
    CheckCircle2,
    Download,
    ExternalLink,
    Film,
    Shield,
    ShoppingBag,
    Sparkles,
    Store,
    Users,
} from "lucide-react";
import { toast } from "sonner";
import { PartnerAPI } from "@/lib/partnerApi";
import ReserveLedger from "@/components/partner/ReserveLedger";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
        });
    } catch {
        return iso;
    }
}

const SERVICE_ICONS = {
    storefront: ShoppingBag,
    marketing: Sparkles,
    creator: Film,
    cyberops: Shield,
    philanthropy: Users,
};

export default function OverviewPanel({ partner }) {
    const [services, setServices] = useState(null);

    useEffect(() => {
        PartnerAPI.fleetServices()
            .then((d) => setServices(d.services || []))
            .catch(() => setServices([]));
    }, []);

    return (
        <section className="space-y-14" data-testid="partner-overview">
            {/* Readiness checklist — payouts, affiliate publishing, and
                advances remain locked until all three steps clear. */}
            <ReadinessChecklist partner={partner} />

            {/* Signed badge */}
            {partner.agreement_accepted && (
            <div
                className="p-6 bg-cream/5 border border-gold/40 rounded-sm flex flex-wrap gap-4 items-center backdrop-blur"
                data-testid="partner-signed-badge"
            >
                <CheckCircle2 size={32} className="text-gold shrink-0" />
                <div className="min-w-0 flex-1">
                    <p className="text-[0.62rem] tracking-[0.3em] uppercase text-gold">
                        Agreement on file
                    </p>
                    <p className="font-serif text-xl text-cream mt-1">
                        NDA + Creator Agreement signed
                    </p>
                    <p className="text-cream/60 text-sm mt-1">
                        Accepted{" "}
                        <span
                            className="text-cream"
                            data-testid="partner-signed-at"
                        >
                            {fmtDate(partner.agreement_accepted_at)}
                        </span>
                    </p>
                    <p
                        className="text-cream/55 text-xs mt-2 leading-snug"
                        data-testid="partner-counter-signatory"
                    >
                        Counter-signed by{" "}
                        <span className="text-cream">
                            {partner.eigoo_signatory || "Dontae Kirk"}
                        </span>{" "}
                        ·{" "}
                        {partner.eigoo_signatory_title || "Director, EIGOO Inc."}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={async () => {
                        try {
                            await PartnerAPI.openMsaExport();
                        } catch {
                            toast.error("Could not open MSA export");
                        }
                    }}
                    data-testid="partner-msa-export-btn"
                    className="btn-pill btn-ghost-light text-xs shrink-0"
                >
                    <Download size={13} /> Export MSA (PDF)
                </button>
            </div>
            )}

            {/* Revenue sharing (NOT equity) */}
            <div>
                <h2 className="font-serif text-3xl text-cream">
                    Revenue Sharing
                </h2>
                <p className="text-cream/60 text-sm mt-1 max-w-2xl">
                    Contractual commission &amp; royalty on{" "}
                    <strong className="text-cream">NET revenue</strong>{" "}
                    (after card fees &amp; COGS) — not an equity assignment.
                    You remain the{" "}
                    <span
                        className="text-gold"
                        data-testid="partner-overview-equity-owner"
                    >
                        100% equity owner of your LLC
                    </span>
                    . Reconciled monthly on the 15th.
                </p>
                <div
                    className="mt-6 grid md:grid-cols-3 gap-5"
                    data-testid="partner-dashboard-split"
                >
                    <SplitCard pct={50} label="Ymaku" sub="Retained · 100% equity owner" emphasis testid="split-ymaku" />
                    <SplitCard pct={40} label="EIGOO Inc." sub="Services commission (not equity)" testid="split-eigoo" />
                    <SplitCard pct={10} label="CyberKids Initiative" sub="Philanthropic royalty" gold testid="split-cyberkids" />
                </div>
                <p
                    className="mt-4 text-cream/50 text-xs max-w-2xl leading-relaxed"
                    data-testid="partner-overview-grant-note"
                >
                    This structure preserves eligibility for minority-,
                    woman-, and Pacific-Islander-owned business grants and
                    certifications that require 51%+ ownership by the
                    qualifying individual.
                </p>
            </div>

            {/* EIGOO reinstatement + pending reserve ledger */}
            <ReserveLedger />

            {/* Fleet services */}
            <div data-testid="partner-fleet-services">
                <h2 className="font-serif text-3xl text-cream">
                    EIGOO Fleet · All Services
                </h2>
                <p className="text-cream/60 text-sm mt-1">
                    Every service in the EIGOO ecosystem — accessible from
                    your dashboard.
                </p>
                <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(services || []).map((s) => {
                        const Icon = SERVICE_ICONS[s.category] || Store;
                        return (
                            <a
                                key={s.id}
                                href={s.url}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`fleet-service-${s.id}`}
                                className="group p-5 bg-cream/5 border border-cream/10 rounded-sm hover:border-gold/60 transition-colors backdrop-blur"
                            >
                                <div className="flex items-center gap-2">
                                    <Icon size={16} className="text-gold" />
                                    <span className="text-[0.6rem] tracking-[0.28em] uppercase text-cream/55">
                                        {s.status === "live" ? "Live" : s.status}
                                    </span>
                                    <ExternalLink
                                        size={11}
                                        className="ml-auto text-cream/40 group-hover:text-gold transition-colors"
                                    />
                                </div>
                                <h3 className="font-serif text-lg text-cream mt-3">
                                    {s.name}
                                </h3>
                                <p className="text-cream/65 text-xs mt-1 leading-relaxed">
                                    {s.tagline}
                                </p>
                                {s.role && (
                                    <p className="text-gold/80 text-[0.62rem] tracking-[0.22em] uppercase mt-3">
                                        {s.role}
                                    </p>
                                )}
                            </a>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function SplitCard({ pct, label, sub, emphasis, gold, testid }) {
    return (
        <div
            data-testid={`partner-dashboard-${testid}`}
            className={`p-7 rounded-sm border ${
                emphasis
                    ? "bg-cream text-forest border-gold"
                    : gold
                    ? "bg-forest border-gold/60"
                    : "bg-cream/5 border-cream/15 backdrop-blur"
            }`}
        >
            <p
                className={`font-serif text-7xl leading-none ${
                    gold ? "text-gold" : emphasis ? "text-forest" : "text-cream"
                }`}
            >
                {pct}%
            </p>
            <p
                className={`mt-4 text-xs tracking-[0.28em] uppercase ${
                    emphasis ? "text-brown" : "text-cream/60"
                }`}
            >
                {label}
            </p>
            <p
                className={`mt-1 text-sm ${
                    emphasis ? "text-brown" : "text-cream/55"
                }`}
            >
                {sub}
            </p>
        </div>
    );
}

function ReadinessChecklist({ partner }) {
    const items = [
        {
            label: "Workspace access",
            ok: true,
            note: "Auto-approved on registration",
            testid: "readiness-workspace",
        },
        {
            label: "Legal name on file",
            ok: !partner.legal_name_pending,
            note: partner.legal_name_pending
                ? "Add your legal name — required for agreements."
                : "On file",
            testid: "readiness-legal-name",
        },
        {
            label: "Identity verified",
            ok: !!partner.identity_verified,
            note: partner.identity_verified
                ? "Verified"
                : "Pending — contact an owner once you're ready to verify.",
            testid: "readiness-identity",
        },
        {
            label: "Creator Agreement signed",
            ok: !!partner.agreement_accepted,
            note: partner.agreement_accepted
                ? "Signed"
                : "Sign to unlock payouts & affiliate publishing.",
            testid: "readiness-agreement",
            href: !partner.agreement_accepted ? "/partner/agreement" : null,
            cta: "Sign now",
        },
        {
            label: "Payment on file",
            ok: !!partner.paid_access,
            note: partner.paid_access
                ? "Active"
                : "Optional — required for paid-tier workspace features.",
            testid: "readiness-payment",
        },
    ];
    const readyForPayouts =
        !!partner.identity_verified && !!partner.agreement_accepted;

    return (
        <div
            className="p-6 bg-cream/5 border border-cream/15 rounded-sm backdrop-blur"
            data-testid="partner-readiness-checklist"
        >
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[0.62rem] tracking-[0.3em] uppercase text-gold">
                        Account readiness
                    </p>
                    <p className="font-serif text-xl text-cream mt-1">
                        {readyForPayouts
                            ? "You're ready for payouts."
                            : "Finish setup to unlock payouts."}
                    </p>
                    <p className="text-cream/55 text-xs mt-1">
                        Payouts, affiliate link publishing, and advances stay
                        locked until identity verification and the signed
                        agreement are complete.
                    </p>
                </div>
                <span
                    className={`text-[0.6rem] tracking-[0.25em] uppercase px-3 py-1 rounded-full ${
                        readyForPayouts
                            ? "bg-olive/25 text-cream"
                            : "bg-gold/25 text-gold"
                    }`}
                    data-testid="partner-readiness-badge"
                >
                    {readyForPayouts ? "Payout-ready" : "In progress"}
                </span>
            </div>
            <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-2.5">
                {items.map((it) => (
                    <div
                        key={it.label}
                        data-testid={`partner-${it.testid}`}
                        className={`px-3 py-3 rounded-sm border text-xs ${
                            it.ok
                                ? "border-olive/40 bg-olive/10 text-cream"
                                : "border-cream/15 bg-forest/40 text-cream/70"
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            {it.ok ? (
                                <CheckCircle2 size={12} className="text-olive shrink-0" />
                            ) : (
                                <span className="w-3 h-3 rounded-full border border-cream/40 inline-block shrink-0" />
                            )}
                            <span className="font-medium">{it.label}</span>
                        </div>
                        <p className="mt-1 text-[0.65rem] leading-relaxed text-cream/60">
                            {it.note}
                        </p>
                        {it.href && (
                            <a
                                href={it.href}
                                data-testid={`${it.testid}-cta`}
                                className="mt-2 inline-block text-[0.6rem] tracking-[0.25em] uppercase text-gold hover:underline"
                            >
                                {it.cta} →
                            </a>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
